import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  Dialog,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { CreateWasteContainerMeasurementDto, HotLabEquipmentDto, HotLabEquipmentType, WasteContainerDto } from '@services/dtos'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { wasteContainerMeasurementSchema } from '@utils/schemas'
import InputField from '../inputs/InputField'
import { FormHeader } from '../forms/FormHeader'
import { getContainerSubTitles } from '@utils/waste.utils'
import { ActivityLevel } from '@services/dtos'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import { HotLabEquipmentTypeLabels } from '@utils/constants'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import HotLabEquipmentForm from '../forms/HotLabEquipmentForm'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import SyncIcon from '@mui/icons-material/Sync'
import { MemberDto, MemberService } from '@services/api'
import { getNameWithRole } from '@utils/member.utils'

type DistancePreset = '0' | '1' | '2' | 'custom'
type MeasurementUnit = 'cps' | 'cpm'

type Props = {
  open: boolean
  container: WasteContainerDto
  onClose: () => void
  title: string
  successMessage: string
  errorMessage: string
  onSubmit: (dto: CreateWasteContainerMeasurementDto) => Promise<unknown>
  forceActivity?: boolean
}

export function WasteContainerMeasurementDialog({ open, container, onClose, title, successMessage, errorMessage, onSubmit, forceActivity }: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [distancePreset, setDistancePreset] = useState<DistancePreset>('0')
  const [unit, setUnit] = useState<MeasurementUnit>('cps')
  const [selectedEquipment, setSelectedEquipment] = useState<HotLabEquipmentDto | null>(null)
  const [selectedMember, setSelectedMember] = useState<MemberDto | null>(null)
  const needsActivity = forceActivity || ((container.activityLevel === ActivityLevel.LOW || container.activityLevel === ActivityLevel.HIGH) && container.wasteEntryCount > 0)

  const defaultMember = user && 'role' in user ? user as unknown as MemberDto : null

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateWasteContainerMeasurementDto>({
    resolver: yupResolver(wasteContainerMeasurementSchema),
    context: { container, forceActivity },
    defaultValues: needsActivity ? { distanceM: 0, measuredById: defaultMember?.id } : {},
  })

  useEffect(() => {
    if (needsActivity) {
      setValue('measuredAt', new Date())
    }
  }, [])

  const fetchCounters = async (search: string) => {
    const page = await hotLabEquipmentService.getAll({
      search,
      types: [HotLabEquipmentType.GEIGER_COUNTER, HotLabEquipmentType.CONTAMINATION_PROBE],
      limit: 20,
      page: 0,
    })
    return page?.datas ?? []
  }

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const handleDistancePreset = (_: React.MouseEvent, value: DistancePreset | null) => {
    if (!value) {
      return
    }
    setDistancePreset(value)
    if (value !== 'custom') {
      setValue('distanceM', Number(value), { shouldValidate: true })
    } else {
      setValue('distanceM', undefined, { shouldValidate: true })
    }
  }

  const toCps = (value: number | undefined): number | undefined => {
    if (value == null) {
      return undefined
    }
    if (unit === 'cpm') {
      return Math.round((value / 60) * 100) / 100
    }
    return value
  }

  const handleFormSubmit = async (data: CreateWasteContainerMeasurementDto) => {
    setIsSubmitting(true)
    const dto: CreateWasteContainerMeasurementDto = needsActivity ? {
      ...data,
      measurementCps: toCps(data.measurementCps),
      backgroundNoiseCps: toCps(data.backgroundNoiseCps),
    } : {}
    onSubmit(dto)
      .then(() => {
        dispatch(enqueueSnackbar({ message: successMessage, options: { variant: 'success' } }))
        onClose()
      })
      .catch(() =>
        dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } })),
      )
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className="flex w-full h-full p-4">
        <form
          onSubmit={(e) => { e.stopPropagation(); handleSubmit(handleFormSubmit)(e) }}
          className="w-full"
        >
          <FormHeader
            isSubmitting={isSubmitting}
            onClose={onClose}
            title={title}
            subTitles={getContainerSubTitles(container)}
          />

          {needsActivity && (
            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Appareil de mesure"
                  addTooltipTitle="Ajouter du matériel"
                  dialogContent={(close) => (<HotLabEquipmentForm onClose={() => close()} />)}
                  formMaxWidth="md"
                  input={
                    <ServerAutocomplete<HotLabEquipmentDto>
                      value={selectedEquipment}
                      fetchService={fetchCounters}
                      onChange={(equipment) => {
                        setSelectedEquipment(equipment)
                        setValue('hotLabEquipmentId', equipment?.id ?? undefined, { shouldValidate: true })
                      }}
                      getValue={(e) => e?.id ?? null}
                      getOptionLabel={(e) => {
                        const eq = e as HotLabEquipmentDto
                        return eq ? `${eq.name} (${HotLabEquipmentTypeLabels[eq.type]})` : ''
                      }}
                      renderOption={(props, eq) => (
                        <li {...props} key={eq.id}>
                          <span>{eq.name}</span>
                          <Typography variant="caption" color="text.secondary" ml={1}>
                            {HotLabEquipmentTypeLabels[eq.type]}
                          </Typography>
                        </li>
                      )}
                      placeholder={`${HotLabEquipmentTypeLabels[HotLabEquipmentType.GEIGER_COUNTER]} ou ${HotLabEquipmentTypeLabels[HotLabEquipmentType.CONTAMINATION_PROBE]}...`}
                      label="Appareil de mesure"
                      minSearchLength={0}
                      error={!!errors.hotLabEquipmentId}
                      helperText={errors.hotLabEquipmentId?.message}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <div className="flex items-center gap-2">
                  <Typography variant="caption" color="text.secondary">Unité</Typography>
                  <ToggleButtonGroup
                    value={unit}
                    exclusive
                    onChange={(_, v) => v && setUnit(v)}
                    size="small"
                  >
                    <ToggleButton value="cps">coups/s</ToggleButton>
                    <ToggleButton value="cpm">coups/min</ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name="measurementCps"
                  label="Mesure"
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputField
                  control={control}
                  name="backgroundNoiseCps"
                  label="Bruit de fond"
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    label="Heure mesure"
                    value={watch('measuredAt') ? moment(watch('measuredAt')) : null}
                    onChange={(date) => {
                      if (!date || !date.isValid()) {
                        setValue('measuredAt', undefined)
                        return
                      }
                      setValue('measuredAt', date.toDate())
                    }}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.measuredAt,
                        helperText: errors.measuredAt?.message,
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Maintenant" arrow>
                                <IconButton size="small" onClick={() => setValue('measuredAt', new Date())}>
                                  <SyncIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ServerAutocomplete<MemberDto>
                  defaultValue={defaultMember}
                  initialOptions={defaultMember ? [defaultMember] : []}
                  value={selectedMember ?? defaultMember}
                  fetchService={fetchMembers}
                  onChange={(value: MemberDto | null) => {
                    setSelectedMember(value)
                    setValue('measuredById', value?.id ?? undefined, { shouldValidate: true })
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => getNameWithRole(value as MemberDto)}
                  debounceMs={300}
                  placeholder="Rechercher un membre..."
                  label="Mesuré par"
                  helperText={errors.measuredById?.message}
                  error={!!errors.measuredById}
                  minSearchLength={0}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Distance
                </Typography>
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  <ToggleButtonGroup
                    value={distancePreset}
                    exclusive
                    onChange={handleDistancePreset}
                    size="small"
                  >
                    <ToggleButton value="0">Contact</ToggleButton>
                    <ToggleButton value="1">1 m</ToggleButton>
                    <ToggleButton value="2">2 m</ToggleButton>
                    <ToggleButton value="custom">Libre</ToggleButton>
                  </ToggleButtonGroup>

                  {distancePreset === 'custom' && (
                    <InputField
                      control={control}
                      name="distanceM"
                      label="Distance (m)"
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ width: 140 }}
                    />
                  )}
                  {distancePreset !== 'custom' && errors.distanceM && (
                    <Typography variant="caption" color="error">{errors.distanceM.message}</Typography>
                  )}
                </div>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </Dialog>
  )
}
